import { useState } from 'react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { dayKey, fmtDate } from '@/shared/lib/format'
import { CODE_LIST_UNAVAILABLE, usePoliticsCodes } from '@/features/polling/useVocabulary'
import { coalitionHistory, getParty, joinCoalition, leaveCoalition, listCoalitions } from './api'
import { PartyFormDialog } from './PartyFormDialog'
import {
  EntityChip, Pill, Swatch, coalitionStatusLabel, count, partyStatusLabel,
  partyStatusTone, roleLabel,
} from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * One party: what it is, whose banner it is under, and everything it has been under.
 *
 * ## Join and leave are the whole point of this panel
 *
 * A party holds AT MOST ONE open coalition membership — a partial unique index
 * enforces it, and the server answers a second join with 409
 * `politics/coalition-open`. So:
 *
 * - **Join is offered whether or not the party is already in one.** Hiding it
 *   while a membership is open would make "you cannot do this yet" and "this
 *   screen is broken" look identical. The refusal names the current coalition and
 *   the join date, which is more than a disabled button could say.
 * - **Every coalition is offered, dissolved ones included.** Joining a dissolved
 *   coalition is refused with `politics/coalition-dissolved` and its dissolution
 *   date. Filtering the list here would be a second copy of a rule the server
 *   already owns, and the two would drift.
 * - **Leaving asks for the date it ended**, defaulted to today in Nairobi rather
 *   than to a UTC slice, which is yesterday's date for three hours every night.
 *
 * ## The history is the record
 *
 * Open and closed memberships, newest first. A party that left one alliance for
 * another is two rows, and neither is deleted — which is what makes "who was in
 * Azimio in 2022" answerable after the fact.
 */
export function PartyDrawer({ partyId, onClose, onChanged }: {
  partyId: string | null
  onClose: () => void
  /** The list behind this drawer holds counts and chips that a write invalidates. */
  onChanged: () => void
}) {
  const canWrite = useCan('politics:party')

  const party = useLoad(
    () => (partyId ? getParty(partyId) : Promise.resolve(null)), [partyId])
  const history = useLoad(
    () => (partyId ? coalitionHistory(partyId) : Promise.resolve([])), [partyId])
  // Loaded for the join picker only, and only while a party is open.
  const coalitions = useLoad(
    () => (partyId ? listCoalitions({}, 0, 200) : Promise.resolve(null)), [partyId])

  const [editing, setEditing] = useState(false)
  const [joining, setJoining] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [coalitionId, setCoalitionId] = useState('')
  const roles = usePoliticsCodes('partyCoalitionRole')
  const [roleCode, setRoleCode] = useState<string>('MEMBER')
  const [joinedOn, setJoinedOn] = useState(() => dayKey(new Date().toISOString()))
  const [leftOn, setLeftOn] = useState(() => dayKey(new Date().toISOString()))
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const row = party.data

  function reloadAll() {
    party.reload()
    history.reload()
    onChanged()
  }

  async function submitJoin() {
    if (!partyId || !coalitionId) return
    setBusy(true)
    setActionError(null)
    setDone(null)
    try {
      const m = await joinCoalition(partyId, { coalitionId, joinedOn, roleCode })
      setJoining(false)
      setDone(`Joined ${m.coalitionName} as ${roleLabel(m.roleCode).toLowerCase()} on ${fmtDate(m.joinedOn)}.`)
      reloadAll()
    } catch (e) {
      // The 409 says which coalition it is already in and when it joined. That
      // sentence is the answer to "why not", so it is rendered verbatim.
      setActionError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  async function submitLeave() {
    if (!partyId) return
    setBusy(true)
    setActionError(null)
    setDone(null)
    try {
      const m = await leaveCoalition(partyId, leftOn || undefined)
      setLeaving(false)
      setDone(`Left ${m.coalitionName} on ${fmtDate(m.leftOn)}. The membership stays in the history.`)
      reloadAll()
    } catch (e) {
      setActionError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const open = party.data?.coalition ?? null
  const rows = [...(history.data ?? [])].sort((a, b) => b.joinedOn.localeCompare(a.joinedOn))

  return (
    <>
      <Drawer
        open={partyId !== null}
        onClose={onClose}
        width="lg"
        title={row ? row.name : 'Party'}
        subtitle={row ? `${row.code}${row.shortName ? ` · ${row.shortName}` : ''}` : undefined}
        right={row ? <Swatch colourHex={row.colourHex} className="h-4 w-4" /> : undefined}
        footer={(
          <>
            {!canWrite && (
              <span className="text-erp-xs text-muted-foreground mr-auto">
                Editing and coalition membership need the <code>politics:party</code> authority.
              </span>
            )}
            <button type="button" className="erp-btn h-9 text-erp-sm" onClick={onClose}>Close</button>
            <button
              type="button"
              className="erp-btn erp-btn-primary h-9 text-erp-sm"
              disabled={!row || !canWrite}
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          </>
        )}
      >
        <ErrorStrip message={party.error ?? history.error} />

        {party.loading && !row ? <LoadingBlock label="Loading the party…" /> : row && (
          <>
            <DrawerSection title="Identity">
              <DrawerField label="Code" value={row.code} mono />
              <DrawerField label="Name" value={row.name} />
              <DrawerField label="Short name" value={row.shortName ?? '—'} />
              <DrawerField label="Colour" value={(
                <span className="inline-flex items-center gap-2">
                  <Swatch colourHex={row.colourHex} />
                  <span className="font-mono text-erp-xs">
                    {row.colourHex ?? 'none declared — every chip for this party renders hollow'}
                  </span>
                </span>
              )} />
              <DrawerField label="Status" value={(
                <Pill tone={partyStatusTone(row.statusCode)}>{partyStatusLabel(row.statusCode)}</Pill>
              )} />
              <DrawerField label="Registered" value={fmtDate(row.registeredOn)} />
              <DrawerField label="Deregistered"
                value={row.deregisteredOn ? fmtDate(row.deregisteredOn) : 'still on the register'} />
              <DrawerField label="Candidates" value={count(row.candidateCount)} />
              {row.notes && <DrawerField label="Notes" value={row.notes} />}
            </DrawerSection>

            <DrawerSection
              title="Coalition"
              right={canWrite && (
                <span className="flex items-center gap-1.5">
                  <button type="button" className="erp-btn h-8 text-erp-xs"
                    onClick={() => { setJoining((j) => !j); setLeaving(false); setActionError(null) }}>
                    Join a coalition
                  </button>
                  <button type="button" className="erp-btn h-8 text-erp-xs"
                    onClick={() => { setLeaving((l) => !l); setJoining(false); setActionError(null) }}>
                    Leave
                  </button>
                </span>
              )}
            >
              <div className="p-4 space-y-3">
                {actionError && <Note tone="bad">{actionError}</Note>}
                {done && <Note tone="good" onDismiss={() => setDone(null)}>{done}</Note>}

                {open ? (
                  <div className="flex flex-wrap items-center gap-2 text-erp-sm">
                    <EntityChip entity={{
                      code: open.coalitionCode,
                      name: open.coalitionName,
                      colourHex: open.coalitionColourHex,
                    }} />
                    <span className="text-muted-foreground">
                      {roleLabel(open.roleCode)} since {fmtDate(open.joinedOn)}
                    </span>
                  </div>
                ) : (
                  <p className="text-erp-sm text-muted-foreground">
                    Independent of any coalition. Its candidates carry no coalition unless one is
                    set on them directly.
                  </p>
                )}

                {joining && (
                  <div className="space-y-2 border-t border-border pt-3">
                    {open && (
                      <Note tone="info">
                        {row.name} already holds an open membership in {open.coalitionName}. A party
                        holds one at a time, so the server will refuse this — record the departure
                        first. The control stays available because the refusal, with its dates, says
                        more than a greyed-out button could.
                      </Note>
                    )}
                    <div className="grid grid-cols-12 gap-2">
                      <select
                        className="erp-input col-span-6"
                        aria-label="Coalition to join"
                        value={coalitionId}
                        onChange={(e) => setCoalitionId(e.target.value)}
                      >
                        <option value="">Choose a coalition…</option>
                        {(coalitions.data?.content ?? []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                            {/* Dissolved ones are offered and marked. The server
                                refuses them by name and date; a filtered list here
                                would be a second copy of that rule. */}
                            {c.statusCode !== 'ACTIVE' ? ` (${coalitionStatusLabel(c.statusCode)})` : ''}
                          </option>
                        ))}
                      </select>
                      <select className="erp-input col-span-3" aria-label="Role in the coalition"
                        value={roleCode}
                        disabled={roles.loading || roles.unavailable}
                        onChange={(e) => setRoleCode(e.target.value)}>
                        {roles.loading && <option value={roleCode}>Loading…</option>}
                        {roles.codes.map((r) => (
                          <option key={r} value={r}>{roleLabel(r)}</option>
                        ))}
                      </select>
                      <DatePicker
                        value={joinedOn}
                        onChange={setJoinedOn}
                        aria-label="Joined on"
                        className="col-span-3"
                      />
                    </div>
                    {roles.unavailable && (
                      // Beside the control it disables, in visible text: a role
                      // list that failed to load and a coalition with no roles
                      // look the same in an empty select, and one of those
                      // readings is that this party cannot join anything.
                      <p className="text-erp-xs text-red-700">
                        {CODE_LIST_UNAVAILABLE}
                        <button type="button" className="erp-btn-ghost h-6 text-erp-xs ml-2"
                          onClick={() => roles.reload()}>
                          Try again
                        </button>
                      </p>
                    )}
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                        onClick={() => setJoining(false)}>Cancel</button>
                      <button type="button" className="erp-btn erp-btn-primary h-8 text-erp-xs"
                        disabled={!coalitionId || !joinedOn || busy} onClick={submitJoin}>
                        {busy ? 'Recording…' : 'Record the membership'}
                      </button>
                    </div>
                  </div>
                )}

                {leaving && (
                  <div className="space-y-2 border-t border-border pt-3">
                    {!open && (
                      <Note tone="info">
                        No open membership to end. The server will say so.
                      </Note>
                    )}
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="text-erp-xs text-muted-foreground space-y-1">
                        <span className="block">Ended on</span>
                        <DatePicker value={leftOn} onChange={setLeftOn} />
                      </label>
                      <span className="text-erp-xs text-muted-foreground pb-2.5">
                        The membership is closed, not deleted — it stays in the history below.
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                          onClick={() => setLeaving(false)}>Cancel</button>
                        <button type="button" className="erp-btn erp-btn-primary h-8 text-erp-xs"
                          disabled={busy} onClick={submitLeave}>
                          {busy ? 'Recording…' : 'Record the departure'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DrawerSection>

            {/* A list, not a table: a `<table>` inside the section's `<dl>` is
                invalid markup, and four columns in a 640px drawer wrap anyway. */}
            <DrawerSection title="Coalition history">
              <div className="px-4 py-2 space-y-2">
                {history.loading && rows.length === 0 ? (
                  <p className="text-erp-sm text-muted-foreground py-2">Loading the history…</p>
                ) : rows.length === 0 ? (
                  <p className="text-erp-sm text-muted-foreground py-2">
                    This party has never been in a coalition.
                  </p>
                ) : rows.map((m) => (
                  <div key={m.id} className="flex flex-wrap items-center gap-2 text-erp-sm">
                    <EntityChip entity={{
                      code: m.coalitionCode, name: m.coalitionName,
                      colourHex: m.coalitionColourHex,
                    }} />
                    <span className="text-erp-xs text-muted-foreground">
                      {roleLabel(m.roleCode)}
                    </span>
                    <span className="ml-auto text-erp-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      {fmtDate(m.joinedOn)} →{' '}
                      {m.open
                        ? <span className="text-emerald-600">open</span>
                        : fmtDate(m.leftOn)}
                    </span>
                  </div>
                ))}
              </div>
            </DrawerSection>
          </>
        )}
      </Drawer>

      <PartyFormDialog
        open={editing}
        party={row}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); reloadAll() }}
      />
    </>
  )
}
